import { useEffect } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import * as Cesium from 'cesium';
import type { TooltipPlacement } from 'antd/es/tooltip';

export interface LayerPopupConfig {
  popupTitle?: string;
  popupTitleUnit?: string;
  defaultPosition?: TooltipPlacement;
  popupCustomStyle?: string;
  popupStyleId?: string | null;
}

export interface UseCesiumPopupArgs {
  isViewerReady: boolean;
  viewerRef: MutableRefObject<Cesium.Viewer | null>;
  layers: any[];
  setPopoverContent: Dispatch<SetStateAction<any>>;
  setPopoverVisible: Dispatch<SetStateAction<boolean>>;
  setClickScreenPos?: (pos: { x: number; y: number }) => void;
  /** Apply the clicked layer's popup chrome (title/style/placement). */
  setActivePopupConfig?: (config: LayerPopupConfig | null) => void;
  highlightFeature?: (feature: any) => void;
  /** When true (e.g. the Create Feature tool is open) the popup click handler
   *  is not attached, so map clicks draw/edit instead of opening a popover. */
  disabled?: boolean;
}

const layerNameFromImagery = (imageryLayer: any): string | null => {
  if (!imageryLayer) return null;
  if (typeof imageryLayer.mapakiLayerName === 'string' && imageryLayer.mapakiLayerName) {
    return imageryLayer.mapakiLayerName;
  }
  // Fallback: WMS provider layers string is usually "mapaki:<uniqueLayerName>"
  const layersParam = imageryLayer?.imageryProvider?.layers ?? imageryLayer?.imageryProvider?._layers ?? null;
  if (typeof layersParam === 'string' && layersParam.length) {
    const last = layersParam.split(',').pop()?.trim() ?? '';
    const bare = last.includes(':') ? last.split(':').pop()! : last;
    return bare || null;
  }
  return null;
};

const resolveLayerConfig = (layers: any[], layerName: string | null) => {
  if (!layerName || !layers?.length) return null;
  const exact = layers.find((l: any) => l?.uniqueLayerName === layerName);
  if (exact) return exact;
  // Tolerate workspace-prefixed or case drift from GetFeatureInfo
  const lower = layerName.toLowerCase();
  return (
    layers.find((l: any) => String(l?.uniqueLayerName ?? '').toLowerCase() === lower) ??
    layers.find((l: any) =>
      String(l?.uniqueLayerName ?? '')
        .toLowerCase()
        .endsWith(lower),
    ) ??
    null
  );
};

export const useCesiumPopup = ({
  isViewerReady,
  viewerRef,
  layers,
  setPopoverContent,
  setPopoverVisible,
  setClickScreenPos,
  setActivePopupConfig,
  highlightFeature,
  disabled,
}: UseCesiumPopupArgs) => {
  useEffect(() => {
    if (!isViewerReady || !viewerRef.current) return;
    if (disabled) {
      setPopoverVisible(false);
      setActivePopupConfig?.(null);
      return;
    }
    // Only attach when at least one layer opts into popups.
    if (!layers?.some((l: any) => l?.popupsEnabled)) {
      setPopoverVisible(false);
      setActivePopupConfig?.(null);
      return;
    }

    const viewer = viewerRef.current;
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

    // Snapshot enabled layers for this handler generation (avoids stale closure races).
    const layersSnapshot = Array.isArray(layers) ? layers : [];

    handler.setInputAction((movement: any) => {
      const pickRay = viewer.camera.getPickRay(movement.position);
      if (!pickRay) return;

      const featuresPromise = viewer.imageryLayers.pickImageryLayerFeatures(pickRay, viewer.scene);

      if (!Cesium.defined(featuresPromise)) {
        setPopoverVisible(false);
        setActivePopupConfig?.(null);
        return;
      }

      Promise.resolve(featuresPromise).then((features) => {
        if (!features?.length) {
          setPopoverVisible(false);
          setActivePopupConfig?.(null);
          return;
        }

        // Prefer the topmost picked feature whose owning layer has popups enabled.
        // pickImageryLayerFeatures returns top-to-bottom in typical Cesium builds.
        let chosen: any = null;
        let layerCfg: any = null;

        for (const feature of features) {
          const name = layerNameFromImagery(feature?.imageryLayer);
          const cfg = resolveLayerConfig(layersSnapshot, name);
          if (cfg?.popupsEnabled) {
            chosen = feature;
            layerCfg = cfg;
            break;
          }
        }

        if (!chosen || !layerCfg) {
          // Click hit only layers with popups off — do not open a global popup.
          setPopoverVisible(false);
          setActivePopupConfig?.(null);
          return;
        }

        setClickScreenPos?.({
          x: movement.position.x,
          y: movement.position.y,
        });

        const props = chosen?.data?.properties ?? chosen?.properties ?? {};
        const id = chosen?.data?.id ?? chosen?.id;

        highlightFeature?.(chosen);
        setActivePopupConfig?.({
          popupTitle: layerCfg.popupTitle ?? '',
          popupTitleUnit: layerCfg.popupTitleUnit ?? '',
          defaultPosition: layerCfg.defaultPosition ?? 'rightTop',
          popupCustomStyle: layerCfg.popupCustomStyle ?? '',
          popupStyleId: layerCfg.popupStyleId ?? null,
        });
        setPopoverContent({ ...props, id, _layerName: layerCfg.uniqueLayerName });
        setPopoverVisible(true);
      });
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    return () => {
      try {
        handler.destroy();
      } catch {
        /* ignore */
      }
    };
  }, [layers, isViewerReady, disabled]);
};
